import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Github, Globe, Database, Code, Cloud } from 'lucide-react';

const Projects: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const projects = [
    {
      title: "GATORXCHANGE – Peer-to-Peer Marketplace",
      type: "Full-Stack Project",
      description: "Architected and deployed a full-stack marketplace with authentication, real-time messaging, reviews, and a points-based transaction system using Django and MySQL.",
      features: [
        "Full-stack marketplace with authentication system",
        "Real-time messaging between users",
        "Review and rating system",
        "Points-based transaction system",
        "Image compression reducing media size by 60%",
        "Indexed search queries with sub-200ms response times",
        "Production deployment on AWS EC2 with Nginx and Gunicorn"
      ],
      technologies: ["Django", "Python", "MySQL", "Tailwind CSS", "Nginx", "Gunicorn", "AWS EC2"],
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      github: "https://github.com/MeetDobariya275",
      live: null,
      category: "Full-Stack"
    },
    {
      title: "BizVista AI – Keyword-Driven Review Intelligence Dashboard",
      type: "AI/ML Project",
      description: "Built a review intelligence dashboard analyzing 100k+ Yelp reviews to identify sentiment trends and operational insights across restaurants.",
      features: [
        "Analyzed 100k+ Yelp reviews for sentiment trends",
        "NLP pipeline using VADER and fuzzy keyword matching",
        "Time-based and business-level filtering with sub-second performance",
        "Local LLM integration for structured summaries and recommendations",
        "Reduced manual review analysis time by 40%"
      ],
      technologies: ["Python", "FastAPI", "SQLite", "Next.js", "React", "Tailwind CSS", "Recharts", "VADER", "rapidfuzz", "Ollama (LLM)"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      github: "https://github.com/MeetDobariya275",
      live: null,
      category: "AI/ML"
    },
    {
      title: "Automated Waste Classification Using Deep Learning",
      type: "Deep Learning Project",
      description: "Fine-tuned ResNet-18 and MobileNetV2 using transfer learning for six-class waste classification, achieving 97% test accuracy and 96.2% macro F1 score.",
      features: [
        "Fine-tuned ResNet-18 and MobileNetV2 models using transfer learning",
        "Six-class waste classification system",
        "Achieved 97% test accuracy and 96.2% macro F1 score",
        "Two-phase training pipeline with selective fine-tuning",
        "Data augmentation for improved generalization",
        "Comprehensive evaluation using confusion matrices and class-level precision/recall analysis"
      ],
      technologies: ["Python", "PyTorch", "ResNet-18", "MobileNetV2", "torchvision", "Google Colab", "NumPy", "scikit-learn"],
      image: "https://images.unsplash.com/photo-1621451537084-482c73073a0f?w=800&h=600&fit=crop",
      github: "https://github.com/MeetDobariya275",
      live: null,
      category: "AI/ML"
    }
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Full-Stack':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'AI/ML':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTechnologyIcon = (tech: string) => {
    if (tech.includes('Django') || tech.includes('Flask') || tech.includes('Python')) {
      return <Code size={16} />;
    } else if (tech.includes('MySQL') || tech.includes('PostgreSQL') || tech.includes('MongoDB')) {
      return <Database size={16} />;
    } else if (tech.includes('EC2') || tech.includes('AWS')) {
      return <Cloud size={16} />;
    } else {
      return <Code size={16} />;
    }
  };

  return (
    <section id="projects" className="py-20 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-white mb-4">Featured Projects</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Showcasing my technical skills through innovative projects that solve real-world problems.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="group"
            >
              <div className="bg-gray-700 rounded-2xl overflow-hidden shadow-xl border border-gray-600 hover:border-blue-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(project.category)}`}>
                      {project.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-800/80 text-gray-300 border border-gray-600">
                      {project.type}
                    </span>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-200">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {project.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={inView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.5, delay: 0.4 + index * 0.2 + idx * 0.1 }}
                          className="flex items-start text-gray-300 text-sm"
                        >
                          <span className="text-blue-400 mr-2 mt-1">•</span>
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, idx) => (
                        <motion.span
                          key={idx}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={inView ? { opacity: 1, scale: 1 } : {}}
                          transition={{ duration: 0.5, delay: 0.6 + index * 0.2 + idx * 0.1 }}
                          className="flex items-center bg-gray-600 text-gray-300 px-3 py-1 rounded-full text-sm font-medium border border-gray-500 hover:border-blue-500 hover:text-blue-400 transition-all duration-200"
                        >
                          {getTechnologyIcon(tech)}
                          <span className="ml-1">{tech}</span>
                        </motion.span>
                      ))}
                    </div>
                  </div>

                  {/* Project Links */}
                  <div className="flex space-x-4">
                    <motion.a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Github size={18} className="mr-2" />
                      View Code
                    </motion.a>
                    {project.live && (
                      <motion.a
                        href={project.live}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Globe size={18} className="mr-2" />
                        Live Demo
                      </motion.a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-gray-400 text-lg mb-6">
            Interested in seeing more of my work?
          </p>
          <motion.a
            href="https://github.com/MeetDobariya275"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-gray-700 hover:bg-gray-600 text-white px-8 py-2 rounded-full font-medium transition-all duration-200"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <Github size={20} className="mr-2" />
            View More on GitHub
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 